/**
 * AdminUsersPage — user management for Shubh Time ops team.
 *
 * Features:
 *  - Server-side search + filter (email, name, provider, plan, status)
 *  - Cursor-based pagination (Load More)
 *  - Inline user detail panel (profile, birth data, subscription, audit log)
 *  - Actions: ban/unban, force logout, delete, grant/revoke premium
 *  - Confirmation dialogs for all destructive actions
 *  - Self-ban prevention
 *  - CSV export of current filtered list
 */
import React, { useState, useCallback, useRef, useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi, type AdminUser } from '@/lib/api'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import {
  Search, Download, RefreshCw, ChevronRight, ChevronDown,
  Ban, LogOut, Trash2, CheckCircle, User, Crown, ShieldOff,
  Loader2, ChevronsUpDown, ChevronUp, Calendar,
} from 'lucide-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// ── Sort helpers ───────────────────────────────────────────────────────────────

type SortKey = 'name' | 'joined' | 'lastActive' | 'plan' | 'status'
type SortDir = 'asc' | 'desc'

function SortIcon({ active, dir }: { col: string; active: boolean; dir: SortDir }) {
  if (!active) return <ChevronsUpDown size={11} className="ml-1 text-star-dim/40 inline-block" />
  return dir === 'asc'
    ? <ChevronUp size={11} className="ml-1 text-gold-dim inline-block" />
    : <ChevronDown size={11} className="ml-1 text-gold-dim inline-block" />
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function exportCSV(users: AdminUser[]) {
  const header = ['ID', 'Name', 'Email', 'Provider', 'Role', 'Status', 'Plan', 'Joined', 'Last Active', 'Activity']
  const rows = users.map(u => [
    u.id, u.name, u.email, u.provider, u.role, u.status,
    u.subscription?.plan ?? 'free',
    dayjs(u.createdAt).format('YYYY-MM-DD'),
    dayjs(u.lastActiveAt).format('YYYY-MM-DD'),
    u.activityCount,
  ])
  const csv = [header, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `shubh-users-${dayjs().format('YYYY-MM-DD')}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function ProviderBadge({ provider }: { provider: AdminUser['provider'] }) {
  const colors: Record<string, string> = {
    google: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    apple:  'text-star-muted bg-star-dim/10 border-star-dim/20',
    email:  'text-gold-dim bg-gold/10 border-gold/20',
    local:  'text-violet-light bg-violet/10 border-violet/20',
  }
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-mono border',
      colors[provider] ?? 'text-star-dim bg-cosmic-void border-cosmic-mist',
    )}>
      {provider}
    </span>
  )
}

function StatusBadge({ status }: { status: AdminUser['status'] }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-medium border',
      status === 'active'
        ? 'bg-auspicious/10 text-auspicious border-auspicious/20'
        : 'bg-inauspicious/10 text-inauspicious border-inauspicious/20',
    )}>
      <span className={cn(
        'w-1.5 h-1.5 rounded-full',
        status === 'active' ? 'bg-auspicious' : 'bg-inauspicious',
      )} />
      {status}
    </span>
  )
}

function PlanBadge({ plan, source }: { plan: 'free' | 'premium'; source?: string | null }) {
  if (plan === 'premium') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-medium border bg-gold/10 text-gold border-gold/30">
        <Crown size={9} />
        PRO{source === 'ops_granted' ? ' (ops)' : ''}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium border bg-cosmic-void text-star-dim border-cosmic-mist">
      FREE
    </span>
  )
}

// ── Confirm dialog (minimal, no external lib dependency) ──────────────────────

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card-cosmic max-w-sm w-full mx-4 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-star-bright">{title}</h3>
        <p className="text-xs text-star-muted">{body}</p>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-xs border border-cosmic-mist text-star-dim hover:text-star-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              danger
                ? 'bg-inauspicious text-white hover:bg-inauspicious/80'
                : 'bg-auspicious/20 text-auspicious border border-auspicious/30 hover:bg-auspicious/30',
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── User detail panel ─────────────────────────────────────────────────────────

function AuditLog({ userId }: { userId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-log', userId],
    queryFn: () => adminApi.getAuditLog(userId),
    retry: 1,
  })

  if (isLoading) return (
    <div className="flex items-center gap-2 text-2xs text-star-dim py-2">
      <Loader2 size={11} className="animate-spin" /> Loading audit log…
    </div>
  )
  if (!data?.length) return (
    <p className="text-2xs text-star-dim py-2">No audit entries.</p>
  )
  return (
    <div className="space-y-1.5 max-h-40 overflow-y-auto">
      {data.map(entry => (
        <div key={entry.id} className="flex items-start gap-2 text-2xs">
          <span className="text-star-dim font-mono whitespace-nowrap">{dayjs(entry.createdAt).format('D MMM HH:mm')}</span>
          <span className="text-star-muted">{entry.action}</span>
          <span className="text-star-dim ml-auto">by {entry.actorName}</span>
        </div>
      ))}
    </div>
  )
}

function UserDetailPanel({
  user,
  isSelf,
  onBanToggle,
  onForceLogout,
  onDelete,
  onGrantPremium,
  onRevokePremium,
  mutating,
}: {
  user: AdminUser
  isSelf: boolean
  onBanToggle: () => void
  onForceLogout: () => void
  onDelete: () => void
  onGrantPremium: () => void
  onRevokePremium: () => void
  mutating: boolean
}) {
  const [confirm, setConfirm] = useState<
    null | 'ban' | 'unban' | 'logout' | 'delete' | 'grant' | 'revoke'
  >(null)

  const sub = user.subscription

  const confirmConfig = {
    ban:    { title: 'Ban user?',           body: `${user.name} will lose access immediately.`,                  confirmLabel: 'Ban',          danger: true  },
    unban:  { title: 'Unban user?',         body: `${user.name} will regain access.`,                            confirmLabel: 'Unban',        danger: false },
    logout: { title: 'Force logout?',       body: `All sessions for ${user.name} will be invalidated.`,          confirmLabel: 'Force logout', danger: true  },
    delete: { title: 'Delete account?',     body: `This will permanently delete ${user.name}'s account and data. This cannot be undone.`, confirmLabel: 'Delete', danger: true  },
    grant:  { title: 'Grant premium?',      body: `${user.name} will get free premium access (ops-granted).`,   confirmLabel: 'Grant',        danger: false },
    revoke: { title: 'Revoke premium?',     body: `${user.name} will lose ops-granted premium access.`,         confirmLabel: 'Revoke',       danger: true  },
  } as const

  const handleConfirm = () => {
    if (!confirm) return
    const actions = {
      ban: onBanToggle, unban: onBanToggle, logout: onForceLogout,
      delete: onDelete, grant: onGrantPremium, revoke: onRevokePremium,
    }
    actions[confirm]?.()
    setConfirm(null)
  }

  return (
    <div className="border-t border-cosmic-mist bg-cosmic-void/40 px-4 py-4 space-y-4">
      {/* Profile + birth data */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-cosmic-nebula rounded-lg px-3 py-2">
          <p className="text-2xs font-mono text-star-dim mb-0.5">Birth Date</p>
          <p className="text-xs text-star">{user.birthDate ?? '—'}</p>
        </div>
        <div className="bg-cosmic-nebula rounded-lg px-3 py-2">
          <p className="text-2xs font-mono text-star-dim mb-0.5">Birth Place</p>
          <p className="text-xs text-star truncate">{user.birthPlace ?? '—'}</p>
        </div>
        <div className="bg-cosmic-nebula rounded-lg px-3 py-2">
          <p className="text-2xs font-mono text-star-dim mb-0.5">Active Dasha</p>
          <p className="text-xs text-star">{user.activeDasha ?? '—'}</p>
        </div>
        <div className="bg-cosmic-nebula rounded-lg px-3 py-2">
          <p className="text-2xs font-mono text-star-dim mb-0.5">Activity</p>
          <p className="text-xs font-mono text-gold-dim">{user.activityCount}</p>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-cosmic-nebula rounded-lg px-3 py-2 flex items-center justify-between gap-4">
        <div>
          <p className="text-2xs font-mono text-star-dim mb-1">Subscription</p>
          <div className="flex items-center gap-2">
            <PlanBadge plan={sub?.plan ?? 'free'} source={sub?.source} />
            {sub?.expiresAt && (
              <span className="text-2xs text-star-dim font-mono">
                expires {dayjs(sub.expiresAt).format('D MMM YYYY')}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {sub?.plan !== 'premium' ? (
            <button
              onClick={() => setConfirm('grant')}
              disabled={mutating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gold/30 text-gold-dim hover:bg-gold/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Crown size={11} /> Grant Premium
            </button>
          ) : sub.source === 'ops_granted' ? (
            <button
              onClick={() => setConfirm('revoke')}
              disabled={mutating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-inauspicious/30 text-inauspicious/70 hover:bg-inauspicious/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldOff size={11} /> Revoke Premium
            </button>
          ) : (
            <span className="text-2xs text-star-dim italic">Stripe-managed</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setConfirm(user.status === 'active' ? 'ban' : 'unban')}
          disabled={mutating || isSelf}
          title={isSelf ? 'Cannot ban yourself' : undefined}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
            user.status === 'active'
              ? 'border-inauspicious/40 text-inauspicious hover:bg-inauspicious/10'
              : 'border-auspicious/40 text-auspicious hover:bg-auspicious/10',
            (mutating || isSelf) && 'opacity-50 cursor-not-allowed',
          )}
        >
          {user.status === 'active'
            ? <><Ban size={12} /> Ban User</>
            : <><CheckCircle size={12} /> Unban User</>}
        </button>

        <button
          onClick={() => setConfirm('logout')}
          disabled={mutating}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gold/30 text-gold-dim hover:bg-gold/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut size={12} /> Force Logout
        </button>

        <button
          onClick={() => setConfirm('delete')}
          disabled={mutating || isSelf}
          title={isSelf ? 'Cannot delete yourself' : undefined}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-inauspicious/30 text-inauspicious/70 hover:bg-inauspicious/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Trash2 size={12} /> Delete Account
        </button>
      </div>

      {/* Audit log */}
      <div>
        <p className="text-2xs font-mono text-star-dim mb-2 uppercase tracking-wider">Recent Audit Log</p>
        <AuditLog userId={user.id} />
      </div>

      {confirm && (
        <ConfirmDialog
          open
          title={confirmConfig[confirm].title}
          body={confirmConfig[confirm].body}
          confirmLabel={confirmConfig[confirm].confirmLabel}
          danger={confirmConfig[confirm].danger}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

const LIMIT = 50

export function AdminUsersPage() {
  const currentUser = useUser()
  const [search, setSearch]               = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [providerFilter, setProviderFilter]   = useState('all')
  const [statusFilter, setStatusFilter]       = useState('all')
  const [planFilter, setPlanFilter]           = useState('all')
  const [dateFrom, setDateFrom]               = useState('')
  const [dateTo, setDateTo]                   = useState('')
  const [sortKey, setSortKey]                 = useState<SortKey>('joined')
  const [sortDir, setSortDir]                 = useState<SortDir>('desc')
  const [expandedId, setExpandedId]           = useState<string | null>(null)
  const queryClient                           = useQueryClient()
  const debounceTimer                         = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 400)
  }, [])

  const toggleSort = useCallback((col: SortKey) => {
    setSortKey(prev => {
      if (prev === col) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        return col
      }
      setSortDir('asc')
      return col
    })
  }, [])

  const queryParams = {
    q:        debouncedSearch || undefined,
    provider: providerFilter,
    status:   statusFilter,
    plan:     planFilter,
    limit:    LIMIT,
  }

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['admin', 'users', queryParams],
    queryFn: ({ pageParam }) =>
      adminApi.getUsers({ ...queryParams, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    retry: 1,
  })

  const rawUsers: AdminUser[] = data?.pages.flatMap(p => p.users) ?? []

  // Client-side date filter + sort (server paginates, client refines)
  const allUsers = useMemo(() => {
    let users = rawUsers
    if (dateFrom) {
      const from = dayjs(dateFrom).startOf('day')
      users = users.filter(u => dayjs(u.createdAt).isAfter(from) || dayjs(u.createdAt).isSame(from))
    }
    if (dateTo) {
      const to = dayjs(dateTo).endOf('day')
      users = users.filter(u => dayjs(u.createdAt).isBefore(to) || dayjs(u.createdAt).isSame(to))
    }
    return [...users].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'name':       cmp = a.name.localeCompare(b.name); break
        case 'joined':     cmp = dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(); break
        case 'lastActive': cmp = dayjs(a.lastActiveAt).unix() - dayjs(b.lastActiveAt).unix(); break
        case 'plan':       cmp = (a.subscription?.plan ?? 'free').localeCompare(b.subscription?.plan ?? 'free'); break
        case 'status':     cmp = a.status.localeCompare(b.status); break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rawUsers, dateFrom, dateTo, sortKey, sortDir])

  const invalidateUsers = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const banMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'ban' | 'unban' }) =>
      adminApi.setUserStatus(id, action),
    onSuccess: invalidateUsers,
  })

  const logoutMutation = useMutation({
    mutationFn: (id: string) => adminApi.forceLogout(id),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => { invalidateUsers(); setExpandedId(null) },
  })

  const grantMutation = useMutation({
    mutationFn: (id: string) => adminApi.grantPremium(id),
    onSuccess: invalidateUsers,
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => adminApi.revokePremium(id),
    onSuccess: invalidateUsers,
  })

  const mutating =
    banMutation.isPending || logoutMutation.isPending ||
    deleteMutation.isPending || grantMutation.isPending || revokeMutation.isPending

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-star-bright">Users</h2>
          <p className="text-xs text-star-muted">
            {isLoading ? 'Loading…' : `${allUsers.length}${hasNextPage ? '+' : ''} users`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCSV(allUsers)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-cosmic-mist text-star-dim hover:text-star-bright hover:border-violet-dark transition-colors"
          >
            <Download size={13} /> Export CSV
          </button>
          <button
            onClick={() => refetch()}
            className={cn('p-2 rounded-lg border border-cosmic-mist text-star-dim hover:text-star-bright transition-colors', isLoading && 'animate-spin')}
            aria-label="Refresh"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Error / demo notice */}
      {isError && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold/5 border border-gold/20">
          <span className="text-gold text-xs">✦</span>
          <span className="text-xs text-star-muted">Could not load users from API. Showing cached or empty state.</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-star-dim" />
          <input
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search by name or email…"
            className="input-cosmic w-full pl-8"
          />
        </div>
        <select
          value={providerFilter}
          onChange={e => setProviderFilter(e.target.value)}
          className="input-cosmic text-xs"
        >
          <option value="all">All providers</option>
          <option value="google">Google</option>
          <option value="apple">Apple</option>
          <option value="email">Email OTP</option>
          <option value="local">Local</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="input-cosmic text-xs"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
        </select>
        <select
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          className="input-cosmic text-xs"
        >
          <option value="all">All plans</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
        {/* Date range filter */}
        <div className="flex items-center gap-1.5">
          <Calendar size={12} className="text-star-dim flex-shrink-0" />
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            title="Joined from"
            className="input-cosmic text-xs w-36"
          />
          <span className="text-star-dim text-xs">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            title="Joined to"
            className="input-cosmic text-xs w-36"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo('') }}
              className="text-2xs text-star-dim hover:text-star-bright transition-colors px-1"
              aria-label="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* User table */}
      <div className="card-cosmic overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-cosmic-mist">
                {([
                  { label: 'Name / Email', col: 'name'       as SortKey },
                  { label: 'Provider',     col: null },
                  { label: 'Role',         col: null },
                  { label: 'Status',       col: 'status'     as SortKey },
                  { label: 'Plan',         col: 'plan'       as SortKey },
                  { label: 'Joined',       col: 'joined'     as SortKey },
                  { label: 'Last Active',  col: 'lastActive' as SortKey },
                  { label: '',             col: null },
                ] as { label: string; col: SortKey | null }[]).map(({ label, col }) => (
                  <th
                    key={label || '_expand'}
                    onClick={col ? () => toggleSort(col) : undefined}
                    className={cn(
                      'px-4 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim whitespace-nowrap',
                      col && 'cursor-pointer hover:text-star-bright select-none transition-colors',
                    )}
                  >
                    {label}
                    {col && <SortIcon col={col} active={sortKey === col} dir={sortDir} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-star-dim">
                    <Loader2 size={18} className="animate-spin inline-block mr-2" />
                    Loading users…
                  </td>
                </tr>
              )}
              {!isLoading && allUsers.map(user => {
                const isExpanded = expandedId === user.id
                const isSelf     = user.id === currentUser?.id
                return (
                  <React.Fragment key={user.id}>
                    <tr
                      className={cn(
                        'border-b border-cosmic-mist/50 hover:bg-cosmic-dusk/30 cursor-pointer transition-colors',
                        isExpanded && 'bg-cosmic-dusk/20',
                      )}
                      onClick={() => setExpandedId(isExpanded ? null : user.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-cosmic-void flex items-center justify-center flex-shrink-0">
                            <User size={13} className="text-star-dim" />
                          </div>
                          <div>
                            <p className="text-star-bright font-medium">
                              {user.name}
                              {isSelf && <span className="ml-1.5 text-2xs text-gold-dim font-mono">(you)</span>}
                            </p>
                            <p className="text-2xs text-star-dim font-mono">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><ProviderBadge provider={user.provider} /></td>
                      <td className="px-4 py-3 font-mono text-star-dim">{user.role}</td>
                      <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
                      <td className="px-4 py-3">
                        <PlanBadge
                          plan={user.subscription?.plan ?? 'free'}
                          source={user.subscription?.source}
                        />
                      </td>
                      <td className="px-4 py-3 text-star-dim font-mono whitespace-nowrap">
                        {dayjs(user.createdAt).format('D MMM YY')}
                      </td>
                      <td className="px-4 py-3 text-star-dim font-mono whitespace-nowrap">
                        {dayjs(user.lastActiveAt).fromNow()}
                      </td>
                      <td className="px-4 py-3 text-star-dim">
                        {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <UserDetailPanel
                            user={user}
                            isSelf={isSelf}
                            mutating={mutating}
                            onBanToggle={() => banMutation.mutate({
                              id: user.id,
                              action: user.status === 'active' ? 'ban' : 'unban',
                            })}
                            onForceLogout={() => logoutMutation.mutate(user.id)}
                            onDelete={() => deleteMutation.mutate(user.id)}
                            onGrantPremium={() => grantMutation.mutate(user.id)}
                            onRevokePremium={() => revokeMutation.mutate(user.id)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
              {!isLoading && allUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-star-dim">
                    No users match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Load more */}
        {hasNextPage && (
          <div className="flex justify-center px-4 py-3 border-t border-cosmic-mist">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border border-cosmic-mist text-star-dim hover:text-star-bright hover:border-violet-dark transition-colors disabled:opacity-50"
            >
              {isFetchingNextPage
                ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
                : 'Load more users'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
